import { MediaDeleteModalRef } from '@components/MediaDeleteModal/MediaDeleteModal';
import { Notification, notificationStore } from '@stores/Notifications';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DeleteBatchModalHost } from './DeleteBatchModalHost';
import { UploadResultsModal } from './UploadResultsModal';

/** Handlers passed down to the upload panel body + its cards. */
export interface UploadPanelHandlers {
  onDismiss: (n: Notification) => void;
  onOpenResults: (notificationId?: string) => void;
  onRetryFailed: (notificationId: string) => void;
  onDeleteBatch: (notificationId: string) => void;
  onCancelBatch: (notificationId: string) => void;
}

/**
 * Owns the upload-activity controller state (results modal, delete-batch modal)
 * and the shared handlers, plus renders those modals. The modals are returned
 * separately from the handlers so the caller mounts them at a PERSISTENT
 * location — outside the collapsible shell (popover / drawer) — otherwise the
 * shell unmounting on close would tear the modal down with it.
 *
 * On mobile all of these open as drawers that STACK above the notifications/
 * uploads drawer (results/delete via MobileDrawer at Z_INDEX.aboveMobileDrawer
 * = 1450), so none of them need to close the underlying drawer first.
 */
export function useUploadActivity(): {
  handlers: UploadPanelHandlers;
  modals: ReactNode;
} {
  const uploadManager = useUploadManager();

  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsFilterId, setResultsFilterId] = useState<string | undefined>(undefined);
  // NOTE: results does NOT call onModalOpen — on mobile the results view is a
  // drawer (FailedMediaListModal) that stacks ABOVE the notifications/uploads
  // drawer, so the underlying drawer must stay open.
  const openResults = useCallback((notificationId?: string) => {
    setResultsFilterId(notificationId);
    setResultsOpen(true);
  }, []);
  const closeResults = useCallback(() => setResultsOpen(false), []);

  const handleRetryFailed = useCallback(
    (notificationId?: string) => {
      uploadManager.retryAllFailures(notificationId);
    },
    [uploadManager],
  );
  const handleCancelBatch = useCallback(
    (notificationId: string) => {
      // Confirmation lives on the card itself (red overlay). By the time this
      // fires, the user has already clicked through it.
      uploadManager.cancelBatch(notificationId);
    },
    [uploadManager],
  );

  // Delete-batch is hosted by DeleteBatchModalHost (mounted only while open)
  // — this hook just tracks which notification id is being deleted.
  const [deleteBatchId, setDeleteBatchId] = useState<string | null>(null);
  const deleteBatchModalRef = useRef<MediaDeleteModalRef | null>(null);
  const handleDeleteBatch = useCallback((notificationId: string) => {
    setDeleteBatchId(notificationId);
  }, []);

  const handleDismiss = useCallback((n: Notification) => {
    if (n.type === 'generic-toast' && n.toastId != null) {
      toast.dismiss(n.toastId);
    }
    notificationStore.remove(n.id);
  }, []);

  const handlers: UploadPanelHandlers = {
    onDismiss: handleDismiss,
    onOpenResults: openResults,
    onRetryFailed: handleRetryFailed,
    onDeleteBatch: handleDeleteBatch,
    onCancelBatch: handleCancelBatch,
  };

  const modals = (
    <>
      <UploadResultsModal
        open={resultsOpen}
        onClose={closeResults}
        filterNotificationId={resultsFilterId}
        onFilterBatchEmpty={() => setResultsFilterId(undefined)}
        onRetryAll={handleRetryFailed}
      />
      {deleteBatchId && (
        <DeleteBatchModalHost
          notificationId={deleteBatchId}
          modalRef={deleteBatchModalRef}
          onClose={() => setDeleteBatchId(null)}
        />
      )}
    </>
  );

  return { handlers, modals };
}
